import React from 'react';
import { Collapse, Alert, Row, Col } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const CALENDAR_MODULE_TYPES = ['time', 'time-matching', 'time-gpt', 'time-gpt-matching', 'event', 'calendly'];

const styles = {
    detailLabel: {
        fontSize: 12,
        color: 'var(--gh-text-secondary, #607D8B)',
        display: 'block',
        marginBottom: 2
    },
    detailValue: {
        fontSize: 13,
        color: 'var(--gh-text-primary, #263238)',
        fontWeight: 500,
        minHeight: 20
    }
};

const flowUsesCalendar = (flowSnapshot) => {
    if (!flowSnapshot?.modules) return false;
    return flowSnapshot.modules.some(m => CALENDAR_MODULE_TYPES.includes(m.reply_type));
};

const formatDuration = (hr, min) => {
    const parts = [];
    if (hr > 0) parts.push(`${hr} hr`);
    if (min > 0) parts.push(`${min} min`);
    return parts.length ? parts.join(' ') : '0 min';
};

const formatScheduleSequence = (seq) => {
    if (seq === 'next-available') return 'Next Available (3 earliest slots)';
    return 'Random (3 random available slots)';
};

const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = dayjs(dateStr);
    return d.isValid() ? d.format('MMM D, YYYY') : '--';
};

const DetailField = ({ label, value }) => (
    <div style={{ minHeight: 42 }}>
        <span style={styles.detailLabel}>{label}</span>
        <div style={styles.detailValue}>{value || '--'}</div>
    </div>
);

const STRATEGY_LABELS = {
    least_loaded: 'Least-loaded',
    even_split: 'Even split'
};

// Scheduling-manager: show whose calendar(s) interviews book onto. Falls back to
// the campaign creator when no owner override was set (the default behaviour).
const SchedulingOwner = ({
    schedulingUserId,
    schedulingUserIds,
    schedulingStrategy,
    schedulingUsers = [],
    creatorName
}) => {
    const nameFor = (id) => {
        const u = schedulingUsers.find((x) => String(x.id) === String(id));
        return u?.name || 'Unknown teammate';
    };

    const rrIds = Array.isArray(schedulingUserIds) ? schedulingUserIds : [];
    let value;
    let label = 'Calendar Owner';

    if (schedulingUserId) {
        value = nameFor(schedulingUserId);
    } else if (rrIds.length) {
        label = `Round-robin — ${STRATEGY_LABELS[schedulingStrategy] || 'Least-loaded'}`;
        value = rrIds.map(nameFor).join(', ');
    } else {
        value = creatorName ? `${creatorName} (campaign creator)` : 'Campaign creator';
    }

    return (
        <div style={{ marginBottom: 12 }}>
            <DetailField label={label} value={value} />
        </div>
    );
};

const SharedFields = ({ settings }) => (
    <Row gutter={[12, 8]}>
        <Col xs={24} sm={8}>
            <DetailField
                label="Interview Duration"
                value={formatDuration(settings.hrDuration ?? 1, settings.minDuration ?? 0)}
            />
        </Col>
        <Col xs={24} sm={8}>
            <DetailField
                label="Candidates per Slot"
                value={settings.maxPerInterviewSlot ?? 1}
            />
        </Col>
        <Col xs={24} sm={8}>
            <DetailField
                label="Scheduling Type"
                value={formatScheduleSequence(settings.scheduleSequence)}
            />
        </Col>
    </Row>
);

const SpecificDatesDisplay = ({ settings }) => {
    const dates = settings.specificDates || [];
    return (
        <>
            <span style={styles.detailLabel}>Scheduled Dates</span>
            <div style={{
                background: 'var(--gh-gray-50, #FAFAFA)',
                border: '1px solid var(--gh-gray-200, #E6E6E6)',
                borderRadius: 6,
                padding: '6px 10px',
                marginBottom: 8
            }}>
                {dates.map((entry, i) => (
                    <div
                        key={i}
                        style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: i < dates.length - 1 ? '1px solid var(--gh-gray-200, #E6E6E6)' : 'none'
                        }}
                    >
                        <span style={{ ...styles.detailValue, minWidth: 100 }}>
                            {formatDate(entry.date)}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--gh-text-secondary, #607D8B)' }}>
                            {entry.startTime && entry.endTime
                                ? `${entry.startTime} – ${entry.endTime}`
                                : '--'}
                        </span>
                    </div>
                ))}
                {!dates.length && <span style={styles.detailValue}>--</span>}
            </div>
            <SharedFields settings={settings} />
        </>
    );
};

const CampaignSchedulingDetails = ({
    availabilitySettings,
    flowSnapshot,
    defaultExpanded = false,
    schedulingUserId,
    schedulingUserIds,
    schedulingStrategy,
    schedulingUsers,
    creatorName
}) => {
    const hasCalendarFlow = flowUsesCalendar(flowSnapshot);

    if (!availabilitySettings && !hasCalendarFlow) return null;

    const mode = availabilitySettings?.mode;

    return (
        <div style={{
            background: '#fff',
            border: '1px solid var(--gh-gray-200, #E6E6E6)',
            borderRadius: 8
        }}>
            <Collapse
                defaultActiveKey={defaultExpanded ? ['scheduling'] : []}
                ghost
                items={[{
                    key: 'scheduling',
                    label: (
                        <span style={{ fontWeight: 500, fontSize: 13 }}>
                            <CalendarOutlined style={{ marginRight: 6 }} />
                            Interview Scheduling Settings
                        </span>
                    ),
                    children: (
                        <div style={{ padding: '0 4px 4px' }}>
                            <SchedulingOwner
                                schedulingUserId={schedulingUserId}
                                schedulingUserIds={schedulingUserIds}
                                schedulingStrategy={schedulingStrategy}
                                schedulingUsers={schedulingUsers}
                                creatorName={creatorName}
                            />
                            {!availabilitySettings && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Using the calendar owner's default settings — available days, times, and a 15-day rolling window from today."
                                />
                            )}
                            {mode === 'specific_dates' && <SpecificDatesDisplay settings={availabilitySettings} />}
                        </div>
                    )
                }]}
            />
        </div>
    );
};

export default CampaignSchedulingDetails;
